import React, { createContext, useContext, useState } from 'react';
import { DataSources } from "Utils";
import { components } from 'react-select';
import { BsSearch } from 'react-icons/bs';
import { FaTrashAlt, FaSave } from 'react-icons/fa';
import { useParams } from 'react-router';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import DiscoveryTooltip from 'DiscoveryTooltip';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AppContext from 'Utils/AppContext';

const DataSourceContext = createContext({connection: {}});

const connectionOptionsMap = getConnectionOptionsMap(DataSources.options);

const Option = ({ children, ...props }) => {
  const data = props.data;
  const value = data && data.value;
  const helperText = data.helperText ? data.helperText : data.value === 's3' ?
    'Connect to an S3 Bucket in AWS.' :
    `Connect to a ${data.label} database.`;

  return (
    <components.Option className='position-relative' {...props}>
      <div className='d-flex flex-row'>
        <div className='mr-2'>
          {data && data.icon}
        </div>
        <div>
          <div>
            {children}
          </div>
          { value &&
            <div className='select-box-helper'>
              {helperText}
            </div>
          }
        </div>
      </div>
    </components.Option>
  );
};

const Input = ({ children, ...props }) => {
  const validValue = props.value && props.value.length && props.value.length > 0;

  return (
    <div 
      className='select-box-input-container'
    >
      { validValue > 0 && <BsSearch className='select-box-search-icon'/> }
      <components.Input className='select-box-input' {...props} />
      { validValue > 0 && (
        <div className='select-box-search-helper-text select-box-helper'>
          Filter by data source type.
        </div>
      )}
    </div>
  )
}

const SingleValue = ({ children, ...props }) => {
  const data = props.data;
  const value = data && data.value;
  const helperText = data.value === 's3' ?
    'Connect to an S3 Bucket in AWS.' :
    `Connect to a ${data.label} database.`;

  return (
    <components.SingleValue className='position-relative d-flex flex-row' {...props}>
      <div className='mr-2'>
        {data && data.icon}
      </div>
      <div>
        <div>
          {children}
        </div>
        { value &&
          <div className='select-box-helper'>
            {helperText}
          </div>
        }
      </div>
    </components.SingleValue>
  )
}


function getConnectionData(id, connections) {
  let connectionValue = {};

  if (id >= 0) {
    connectionValue = { ...(connections.find((obj) => obj.id == id)) } || {};
    const typeValue = connectionValue.type in connectionOptionsMap ? connectionValue.type : 'jdbc';
    connectionValue.type = typeValue;
  }

  return connectionValue;
}

function getConnectionOptionsMap(options) {
  let map = {};
  options.forEach((option) => map[option.value] = true);
  return map;
}

const getConnectionRedirectHandler = (connections, history) => (connection) => {
  if (connection?.id != null) {
    history.push(`/sources/source-editor/${encodeURIComponent(connection.id)}`);
    return;
  }
  // Fallback: caller passed an unsaved connection — find the row that matches
  // by name. Once name is no longer load-bearing this branch can be deleted.
  const targetConnection = (connections || []).find(c => c.name === connection?.name);
  if (targetConnection?.id != null) {
    history.push(`/sources/source-editor/${encodeURIComponent(targetConnection.id)}`);
  }
}

const customFilter = (option, searchText) => {
  const label = option.data && option.data.label && option.data.label.toLowerCase();
  const type = option.value && option.value.type && option.value.type.toLowerCase();
  const database = option.value && option.value.database && option.value.database.toLowerCase();

  const searchTerms = searchText.toLowerCase();
  const searchedByLabel = label && label.includes(searchTerms);
  const searchedByType = type && type.includes(searchTerms);
  const searchedByDatabase = database && database.includes(searchTerms);

  return (searchedByLabel || searchedByType || searchedByDatabase);
}

const FormButtons = ({ deleteDataSourceHandler, connection }) => {
  const { id } = useParams();
  const { currentState, saving, fetched } = useContext(DataSourceContext);
  const { isAdmin } = useContext(AppContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const saveEnabled = !id || fetched;
  const lockedForNonAdmin = !isAdmin && connection && connection.readOnly;
  // Delete is locked for EVERYONE while the source is read only (the server returns 403
  // regardless of role) — an admin must switch Read Only off and save first.
  const deleteLocked = !!(connection && connection.readOnly);
  const dsRef = connection && (connection.tableData && connection.tableData.id != null
    ? { id: connection.tableData.id, name: connection.name }
    : (connection.id != null ? { id: connection.id, name: connection.name } : null));
  return (
    <Row className='mt-5 justify-content-end'>
    { id &&
      <div className='pr-1'>
        <DiscoveryTooltip
          text={deleteLocked
            ? (isAdmin
              ? 'This data source is read only. Turn off Read Only and save before deleting.'
              : 'This data source is read only.')
            : 'Delete this data source.'}
        >
          <Button
            disabled={currentState === 'deleting' || deleteLocked}
            className='w-100'
            onClick={() => setShowDeleteModal(true)}
            variant='outline-light'
          >
            { currentState === 'deleting' ? (
              <Spinner
                className='mr-2'
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
              />
            ) : (
              <FaTrashAlt className='mr-2' />
            )}
            DELETE
          </Button>
        </DiscoveryTooltip>
        <DeleteConfirmationModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          dataSource={dsRef}
          onConfirm={async () => {
            setShowDeleteModal(false);
            await deleteDataSourceHandler(connection);
          }}
        />
      </div>}
      <div className={id && 'pl-1'}>
        <DiscoveryTooltip
          placement='right'
          text={lockedForNonAdmin ? 'This data source is read only.' : (id ? 'Update Data Source details.' : 'Save your Data Source')}
        >
          <Button
            className='w-100'
            variant='primary primary'
            disabled={saving || !saveEnabled || lockedForNonAdmin}
            // onClick={async () => await saveDataSourceHandler(connection)}
            type='submit'
            data-testid="source-editor-save"
          >
            { saving ? (
              <Spinner
                className='mr-2'
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
              />
            ) : (
              <FaSave className='mr-2' />
            )}
            SAVE
          </Button>
        </DiscoveryTooltip>
      </div>
    </Row>
  )
}

const getReactSelectOption = (type, options) => (options.find((item) => item.value === type)) || (options[options.length - 1]);

export {
  customFilter,
  DataSourceContext,
  FormButtons,
  getReactSelectOption,
  getConnectionData,
  getConnectionRedirectHandler,
  Input,
  Option,
  SingleValue
}